import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowDown, ArrowUp, ListVideo, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { BulkBar, toggleAll, toggleId } from '../components/BulkBar';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import {
  useBulkDeletePlaylists,
  useCreatePlaylist,
  useDeletePlaylist,
  usePlaylists,
  useRefreshPlaylist,
  useUpdatePlaylist,
} from '../lib/api/playlists';
import type { PlaylistInput, PublicPlaylist } from '../lib/types';

/**
 * "Curated playlists" admin — pastes a YouTube playlist id (the part after
 * `?list=` in the URL); the API hits YouTube once on save to populate title
 * + thumbnail + item count, so the mobile read path is DB-only. Reorder via
 * the up/down arrows in the table; the mobile Playlists tab honours the
 * `sortOrder` column.
 */
const playlistSchema = z.object({
  youtubePlaylistId: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'invalid YouTube playlist id'),
  title: z
    .string()
    .max(512)
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v.trim() : null)),
  description: z
    .string()
    .max(8000)
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v.trim() : null)),
  imageUrl: z
    .string()
    .url('must be a URL')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  isActive: z.boolean().default(true),
});

type PlaylistForm = z.input<typeof playlistSchema>;

export default function Playlists(): JSX.Element {
  const [editing, setEditing] = useState<PublicPlaylist | 'new' | null>(null);
  const lastDialogKey = useRef('closed');
  if (editing !== null) lastDialogKey.current = editing === 'new' ? 'new' : editing.id;
  const dialogKey = lastDialogKey.current;
  const [confirmDelete, setConfirmDelete] = useState<PublicPlaylist | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const playlistsQ = usePlaylists();
  const create = useCreatePlaylist();
  const update = useUpdatePlaylist();
  const refresh = useRefreshPlaylist();
  const del = useDeletePlaylist();
  const bulkDelete = useBulkDeletePlaylists();
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const items = playlistsQ.data?.items ?? [];
  const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id));
  const pageIds = sortedItems.map((p) => p.id);

  const moveSort = async (row: PublicPlaylist, direction: -1 | 1) => {
    const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id));
    const idx = sorted.findIndex((p) => p.id === row.id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    // Swap their sort orders. If they happened to share the same value
    // (two newly-added playlists both at 0), give them different ones.
    const aSort = a.sortOrder;
    const bSort = b.sortOrder === a.sortOrder ? a.sortOrder + 1 : b.sortOrder;
    await Promise.all([
      update.mutateAsync({ id: a.id, input: { sortOrder: bSort } }),
      update.mutateAsync({ id: b.id, input: { sortOrder: aSort } }),
    ]);
  };

  return (
    <>
      <Topbar title="Playlists" subtitle="Curated YouTube playlists for the mobile Watch tab" />
      <PageHeader
        title="Playlists"
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> New playlist
          </Button>
        }
      />
      <div className="px-6 pb-10">
        {playlistsQ.isLoading ? (
          <Spinner label="Loading playlists…" />
        ) : items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                      onChange={() => setSelected(toggleAll(selected, pageIds))}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="w-12">#</th>
                  <th>Playlist</th>
                  <th>YouTube ID</th>
                  <th className="w-28 text-right">Items</th>
                  <th className="w-28 text-center">Active</th>
                  <th className="w-56" />
                </tr>
              </thead>
              <tbody>
                {[...items]
                  .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id))
                  .map((p, idx, arr) => (
                    <tr key={p.id} className={selected.has(p.id) ? 'bg-brand/5' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selected.has(p.id)}
                          onChange={() => setSelected(toggleId(selected, p.id))}
                          aria-label={`Select ${p.youtubePlaylistId}`}
                        />
                      </td>
                      <td className="font-mono text-xs text-ink-subtle">{idx + 1}</td>
                      <td>
                        <div className="flex items-center gap-3">
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt=""
                              className="h-10 w-16 rounded-sm object-cover"
                            />
                          ) : (
                            <div className="grid h-10 w-16 place-items-center rounded-sm bg-surface-muted text-ink-subtle">
                              <ListVideo className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-medium text-ink">{p.title}</div>
                            {p.description ? (
                              <div className="truncate text-xs text-ink-subtle">{p.description}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <a
                          href={p.youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs text-brand hover:underline"
                        >
                          {p.youtubePlaylistId}
                        </a>
                      </td>
                      <td className="text-right tabular-nums">{p.itemCount}</td>
                      <td className="text-center">
                        {p.isActive ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="neutral">Hidden</Badge>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={idx === 0 || update.isPending}
                            onClick={() => moveSort(p, -1)}
                            aria-label="Move up"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={idx === arr.length - 1 || update.isPending}
                            onClick={() => moveSort(p, 1)}
                            aria-label="Move down"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={refresh.isPending}
                            onClick={async () => {
                              try {
                                await refresh.mutateAsync(p.id);
                                showToast({ title: 'Refreshed from YouTube', variant: 'success' });
                              } catch (err) {
                                showToast({
                                  title: 'Refresh failed',
                                  description: (err as Error).message,
                                  variant: 'danger',
                                });
                              }
                            }}
                            aria-label="Refresh metadata"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditing(p)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDelete(p)}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty
            title="No playlists yet"
            description="Paste a YouTube playlist URL to feature it on the mobile Watch tab."
            action={
              <Button onClick={() => setEditing('new')}>
                <Plus className="h-4 w-4" /> New playlist
              </Button>
            }
          />
        )}
      </div>

      <PlaylistDialog
        // Remount per record — see the note on Channels' dialog.
        key={dialogKey}
        open={editing !== null}
        existing={editing && editing !== 'new' ? editing : null}
        busy={create.isPending || update.isPending}
        onClose={() => setEditing(null)}
        onSubmit={async (input) => {
          try {
            if (editing && editing !== 'new') {
              await update.mutateAsync({ id: editing.id, input });
              showToast({ title: 'Playlist updated', variant: 'success' });
            } else {
              await create.mutateAsync(input);
              showToast({ title: 'Playlist added', variant: 'success' });
            }
            setEditing(null);
          } catch (err) {
            showToast({
              title: 'Save failed',
              description: (err as Error).message,
              variant: 'danger',
            });
          }
        }}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete playlist?</DialogTitle>
            <DialogDescription>
              Removes <strong>{confirmDelete?.title}</strong> from the mobile Watch tab. Doesn't
              affect the playlist on YouTube.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await del.mutateAsync(confirmDelete.id);
                  showToast({ title: 'Playlist deleted', variant: 'success' });
                  setConfirmDelete(null);
                } catch (err) {
                  showToast({
                    title: 'Delete failed',
                    description: (err as Error).message,
                    variant: 'danger',
                  });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkBar
        selected={selected}
        onClear={() => setSelected(new Set())}
        entityLabel="playlist"
        deleting={bulkDelete.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Delete ${selected.size} playlist(s)?`,
              description: 'Removes the selected playlists from the Watch tab. This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDelete.mutateAsync([...selected]);
            showToast({ title: `${result.deleted} playlist(s) deleted`, variant: 'success' });
            setSelected(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </>
  );
}

interface PlaylistDialogProps {
  open: boolean;
  existing: PublicPlaylist | null;
  onClose: () => void;
  onSubmit: (input: PlaylistInput) => void | Promise<void>;
  busy: boolean;
}

function PlaylistDialog({
  open,
  existing,
  onClose,
  onSubmit,
  busy,
}: PlaylistDialogProps): JSX.Element {
  const form = useForm<PlaylistForm>({
    resolver: zodResolver(playlistSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: existing
      ? {
          youtubePlaylistId: existing.youtubePlaylistId,
          title: existing.title ?? '',
          description: existing.description ?? '',
          imageUrl: existing.imageUrl ?? '',
          isActive: existing.isActive,
        }
      : {
          youtubePlaylistId: '',
          title: '',
          description: '',
          imageUrl: '',
          isActive: true,
        },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit playlist' : 'New playlist'}</DialogTitle>
          <DialogDescription>
            On save we fetch the playlist title, description, and thumbnail from YouTube.
            Override below if you want a custom presentation on mobile.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((values) => {
            const parsed = playlistSchema.parse(values);
            void onSubmit(parsed);
          })}
          className="grid gap-4"
        >
          <div>
            <Label htmlFor="pl-id">YouTube playlist ID</Label>
            <Input
              id="pl-id"
              {...form.register('youtubePlaylistId')}
              disabled={!!existing}
              placeholder="PLxxxxxxxxxxxxxxxxxx"
            />
            <p className="mt-1 text-xs text-ink-subtle">
              The part of the playlist URL after <code className="font-mono">?list=</code>. Editable
              only at create time — to change the source, delete and re-create.
            </p>
            {form.formState.errors.youtubePlaylistId ? (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.youtubePlaylistId.message}
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="pl-title">Display title (override)</Label>
            <Input
              id="pl-title"
              {...form.register('title')}
              placeholder="Leave blank to use the YouTube title"
            />
          </div>

          <div>
            <Label htmlFor="pl-desc">Description (override)</Label>
            <Textarea id="pl-desc" rows={3} {...form.register('description')} />
          </div>

          <div>
            <Label htmlFor="pl-img">Thumbnail URL (override)</Label>
            <Input
              id="pl-img"
              {...form.register('imageUrl')}
              placeholder="https://…"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
            <Label htmlFor="pl-active" className="cursor-pointer">
              Show on mobile (Watch → Playlists)
            </Label>
            <Switch
              id="pl-active"
              checked={form.watch('isActive') ?? true}
              onCheckedChange={(v) => form.setValue('isActive', v, { shouldDirty: true })}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : existing ? 'Save changes' : 'Add playlist'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
